import { ref } from 'vue';

/**
 * Global undo toast state. A singleton queue — only one toast is shown
 * at a time; a newer `show()` replaces the previous one. Parents call
 * `show({ message, undo })` right after an optimistic update; the user
 * has 5 seconds to undo.
 *
 * Implementation note: this is a plain module-level ref rather than a
 * Pinia store because the toast has no persistence concerns and no
 * need to hot-reload. The global UndoToast.vue component binds to this
 * shared state.
 */

export interface UndoToastEntry {
  id: number;
  message: string;
  undo: () => void | Promise<void>;
  durationMs: number;
}

const current = ref<UndoToastEntry | null>(null);
let nextId = 1;
let hideTimer: ReturnType<typeof setTimeout> | null = null;

export function useUndoToast() {
  function show(options: { message: string; undo: () => void | Promise<void>; durationMs?: number }) {
    const entry: UndoToastEntry = {
      id: nextId++,
      message: options.message,
      undo: options.undo,
      durationMs: options.durationMs ?? 5000,
    };
    current.value = entry;
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      if (current.value?.id === entry.id) current.value = null;
    }, entry.durationMs);
  }

  function dismiss() {
    if (hideTimer) clearTimeout(hideTimer);
    current.value = null;
  }

  async function triggerUndo() {
    const entry = current.value;
    if (!entry) return;
    dismiss();
    await entry.undo();
  }

  return { current, show, dismiss, triggerUndo };
}
